import { useLang } from '../i18n';

/**
 * Kitchen-sink blog content for the Fluid Field work. Every piece of body
 * copy is bilingual via the local `L(en, zh)` helper. Code blocks and tags
 * stay in English (universal idiom for GLSL/JS).
 */
export default function FluidFieldDetail() {
  const { lang } = useLang();
  const L = (en: string, zh: string) => (lang === 'zh' ? zh : en);

  return (
    <>
      <p className="lead">
        {L(
          'A real-time GLSL fluid simulation that runs entirely on the GPU. Mouse pressure feeds an Eulerian velocity field; the field advects, diffuses, and projects itself once per frame through a pair of feedback FBOs.',
          '一个完全运行在 GPU 上的实时 GLSL 流体模拟。鼠标压力注入欧拉速度场,该速度场每帧通过一对反馈 FBO 完成对流、扩散和投影。',
        )}
      </p>

      <h2>{L('Concept', '原理')}</h2>

      <p>
        {L(
          'The technique follows the ',
          '该技术沿用了 ',
        )}
        <strong>{L('stable-fluids', 'stable-fluids')}</strong>
        {L(
          ' formulation popularized by Jos Stam in the early 2000s, but reorganized so that every step lives in a fragment shader. Two render targets ',
          ' 思路 — 由 Jos Stam 在 2000 年代初推广 — 但被重新组织,使每一步都在片元着色器中完成。两张渲染目标 ',
        )}
        <em>{L('FBO A', 'FBO A')}</em>
        {L(' and ', ' 与 ')}
        <em>{L('FBO B', 'FBO B')}</em>
        {L(
          ' ping-pong: one is read while the other is written, and the roles swap each frame.',
          ' 之间进行 ping-pong:一张被读取的同时另一张被写入,每帧交换角色。',
        )}
      </p>

      <figure>
        <img
          src="/figure-fluid-feedback.svg"
          alt={L('FBO ping-pong feedback chain', 'FBO ping-pong 反馈链路')}
        />
        <figcaption>
          {L(
            'Figure 1 — the feedback chain. Mouse input is rasterized as a force splat into the velocity buffer, then advection, diffusion, and projection passes update the field before it is read back next frame.',
            '图 1 — 反馈链路。鼠标输入被栅格化为力的 splat 注入速度缓冲区,随后通过对流、扩散与投影的若干通道更新场,在下一帧再次读取。',
          )}
        </figcaption>
      </figure>

      <p>
        {L(
          "What you see on screen is just the magnitude of velocity (or pressure, optionally) sampled out of the final FBO. Everything else — the swirling, the dissipation, the way the splat folds back on itself — is emergent from the math.",
          '屏幕上看到的只是从最终 FBO 中采样出的速度(或可选地,压力)的幅值。其余的一切 — 涡旋、耗散、splat 折叠回自身的形态 — 都是从数学中自然涌现出来的。',
        )}
      </p>

      <h2>{L('Implementation', '实现')}</h2>

      <h3>{L('1. Velocity advection', '一、速度对流')}</h3>

      <p>
        {L(
          'Each fragment looks back along its velocity vector by ',
          '每个片元沿其速度向量回溯一个时间步 ',
        )}
        <code>dt</code>
        {L(
          ' and samples whatever was at that position in the previous frame. This is the ',
          ' 并采样上一帧该位置的内容。这就是 ',
        )}
        <strong>{L('semi-Lagrangian', '半拉格朗日')}</strong>
        {L(
          ' step — unconditionally stable, slightly diffusive, and cheap.',
          ' 法 — 无条件稳定,略带扩散,代价低廉。',
        )}
      </p>

      <pre><code className="language-glsl">{`#version 300 es
precision highp float;

uniform sampler2D uVelocity;
uniform vec2  uTexel;   // 1.0 / resolution
uniform float uDt;
uniform float uDissipation;

in  vec2 vUV;
out vec4 fragColor;

void main() {
  vec2 v   = texture(uVelocity, vUV).xy;
  vec2 src = vUV - v * uDt * uTexel;
  vec4 q   = texture(uVelocity, src);
  fragColor = q * uDissipation;
}`}</code></pre>

      <h3>{L('2. Pressure projection', '二、压力投影')}</h3>

      <p>
        {L(
          "After advection the velocity field is no longer divergence-free, so we solve a Poisson equation for pressure and subtract its gradient. We approximate the solve with ",
          '对流后速度场不再无散,因此我们求解压力的 Poisson 方程并减去其梯度。我们用 ',
        )}
        <strong>{L('Jacobi iteration', 'Jacobi 迭代')}</strong>
        {L(
          ' — one render pass per iteration, typically ~30 iterations.',
          ' 来近似该解 — 每次迭代一次渲染通道,通常约 30 次迭代。',
        )}
      </p>

      <pre><code className="language-typescript">{`function projectPressure(gl: WebGL2RenderingContext, iters = 30) {
  for (let i = 0; i < iters; i++) {
    bindFBO(pressurePing);
    useShader(jacobiPressure);
    setUniform('uPressure', pressurePong);
    setUniform('uDivergence', divergenceTex);
    drawQuad();
    [pressurePing, pressurePong] = [pressurePong, pressurePing];
  }
  // subtract gradient
  bindFBO(velocityOut);
  useShader(subtractGradient);
  drawQuad();
}`}</code></pre>

      <blockquote>
        <p>
          {L(
            'Fluid simulation on the GPU is largely a story of trading correctness for steps per frame. Pick the right tradeoff and the math hides; pick the wrong one and the math screams.',
            'GPU 上的流体模拟,本质上是在「精确性」与「每帧步数」之间做交换。交换得当,数学藏在画面之后;交换失当,数学便会从画面里嘶吼出来。',
          )}
        </p>
        <p>
          <em>— {L('field notes, 2025', '研发札记,2025')}</em>
        </p>
      </blockquote>

      <h3>{L('3. Boundaries', '三、边界处理')}</h3>

      <p>
        {L(
          'Edge fragments need special-case kernels. Three patterns cover the common cases:',
          '边缘片元需要特殊的内核处理,以下三种模式涵盖了常见情况:',
        )}
      </p>

      <ol>
        <li>
          {L(
            'For velocity, mirror with negative sign (no-slip wall).',
            '对于速度,沿法向取负镜像(无滑移墙)。',
          )}
        </li>
        <li>
          {L(
            'For pressure, mirror with positive sign (Neumann condition).',
            '对于压力,沿法向取正镜像(Neumann 条件)。',
          )}
        </li>
        <li>
          {L(
            'For dye / scalars, ',
            '对于染色 / 标量场,',
          )}
          <s>{L('use a clamp', '使用 clamp')}</s>
          {' '}
          {L(
            '— actually, sample with a small inset to avoid bilinear contamination.',
            ' — 实际上要带一点点内缩采样,以避免双线性插值带来的污染。',
          )}
        </li>
      </ol>

      <hr />

      <h2>{L('Performance', '性能')}</h2>

      <p>
        {L(
          'Numbers from a 60-frame rolling average on the same input scene. Discrete GPU is an Apple M3 Max; integrated is an M2 base.',
          '数据来自相同输入场景下 60 帧的滚动平均。独立 GPU 为 Apple M3 Max,集成 GPU 为 M2 基础版。',
        )}
      </p>

      <table>
        <thead>
          <tr>
            <th>{L('Resolution', '分辨率')}</th>
            <th>{L('Discrete GPU', '独立 GPU')}</th>
            <th>{L('Integrated GPU', '集成 GPU')}</th>
            <th>{L('Notes', '备注')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>256²</code></td>
            <td>240 fps</td>
            <td>165 fps</td>
            <td>{L('reference budget', '基准目标')}</td>
          </tr>
          <tr>
            <td><code>512²</code></td>
            <td>144 fps</td>
            <td>60 fps</td>
            <td>{L('typical demo target', '常见演示档位')}</td>
          </tr>
          <tr>
            <td><code>1024²</code></td>
            <td>60 fps</td>
            <td>22 fps</td>
            <td>{L('fans spin up', '风扇会被叫醒')}</td>
          </tr>
          <tr>
            <td><code>2048²</code></td>
            <td>22 fps</td>
            <td>—</td>
            <td>{L('memory-bandwidth bound', '受显存带宽限制')}</td>
          </tr>
        </tbody>
      </table>

      <figure>
        <img
          src="/figure-pressure-field.svg"
          alt={L('abstract pressure field swirl', '抽象压力场涡旋')}
        />
        <figcaption>
          {L(
            'Figure 2 — a frame from the pressure buffer at 512², visualized with a grayscale palette.',
            '图 2 — 来自 512² 压力缓冲的一帧,使用灰阶色板可视化。',
          )}
        </figcaption>
      </figure>

      <h2>{L('Glossary', '术语表')}</h2>

      <dl>
        <dt>WebGL2</dt>
        <dd>
          {L(
            'Required for the EXT_color_buffer_float extension; without it, the velocity FBO clamps to 8-bit and the simulation falls apart in seconds.',
            '为启用 EXT_color_buffer_float 扩展所必需;否则速度 FBO 将被限制为 8 位,模拟会在数秒内崩坏。',
          )}
        </dd>
        <dt>{L('vec4 channels', 'vec4 通道')}</dt>
        <dd>
          {L(
            'rgb stores the velocity vector (x, y, ignored z); the alpha channel optionally carries dye density for visualization.',
            'rgb 存储速度向量(x、y,z 忽略);alpha 通道可选地携带染色密度用于可视化。',
          )}
        </dd>
        <dt>{L('dt', '时间步 dt')}</dt>
        <dd>
          {L(
            'Per-frame timestep. Pin to 1/60 for stable visuals or to the wall clock for adaptive playback.',
            '每帧时间步。固定为 1/60 可获得稳定画面,也可绑定真实墙钟实现自适应播放。',
          )}
        </dd>
      </dl>

      <h2>{L('Try it yourself', '本地运行')}</h2>

      <ul>
        <li>
          {L('Make sure you have ', '确保已安装 ')}
          <code>node ≥ 20</code>
          {L(' and a WebGL2-capable browser.', ' 与支持 WebGL2 的浏览器。')}
        </li>
        <li>
          {L('Clone the repository.', '克隆仓库。')}
        </li>
        <li>
          {L('Run ', '执行 ')}
          <code>npm install</code>
          {L(' then ', ' 然后 ')}
          <code>npm run dev</code>
          {L('.', '。')}
        </li>
        <li>
          {L(
            'Open the simulation route and drag the cursor across the canvas.',
            '打开模拟路由,在画布上拖拽光标。',
          )}
        </li>
      </ul>

      <hr />

      <h2>{L('References', '参考资料')}</h2>

      <ul>
        <li>
          Stam, J.{' '}
          <a
            href="https://www.dgp.toronto.edu/~stam/reality/Research/pdf/GDC03.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <em>Real-Time Fluid Dynamics for Games</em>
          </a>{' '}
          (GDC 2003).
        </li>
        <li>
          Harris, M.{' '}
          <a
            href="https://developer.nvidia.com/gpugems/gpugems/part-vi-beyond-triangles/chapter-38-fast-fluid-dynamics-simulation-gpu"
            target="_blank"
            rel="noopener noreferrer"
          >
            <em>Fast Fluid Dynamics Simulation on the GPU</em>
          </a>{' '}
          — GPU Gems 1, Chapter 38.
        </li>
        <li>
          {L('PavelDoGreat, ', 'PavelDoGreat,')}
          <a
            href="https://github.com/PavelDoGreat/WebGL-Fluid-Simulation"
            target="_blank"
            rel="noopener noreferrer"
          >
            WebGL-Fluid-Simulation
          </a>
          {L(' — a great reference implementation.', ' — 一个很好的参考实现。')}
        </li>
      </ul>
    </>
  );
}
